import { useState, useEffect } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';
import { usePWA } from '@/hooks/usePWA';

const InstallPrompt = () => {
  const { isInstallable, isInstalled, isIOS, isStandalone, installApp } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if user is on mobile device
    const checkMobile = () => {
      const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        (window.innerWidth <= 768);
      setIsMobile(mobileCheck);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Check if user has dismissed the prompt before
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
      }
    }

    // Show prompt after a delay for better UX
    const timer = setTimeout(() => {
      if (!isInstalled && !isStandalone && !dismissed) {
        setShowPrompt(true);
      }
    }, 3000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkMobile);
    };
  }, [isInstalled, isStandalone, dismissed]);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleInstall = async () => {
    if (isIOS) {
      // Can't programmatically install on iOS, show instructions
      return;
    }
    
    const success = await installApp();
    if (success) {
      setShowPrompt(false);
    }
  };

  // Only show on mobile devices
  if (!isMobile || !showPrompt || isInstalled || isStandalone) return null;

  // iOS-specific prompt with instructions
  if (isIOS) {
    return (
      <div className="install-prompt install-prompt-ios">
        <button onClick={handleDismiss} className="install-prompt-close">
          <X className="w-4 h-4" />
        </button>
        
        <div className="install-prompt-content">
          <div className="install-prompt-icon">
            <Download className="w-6 h-6" />
          </div>
          
          <div className="install-prompt-text">
            <h3 className="install-prompt-title">Install Shadesense on your phone</h3>
            <p className="install-prompt-desc">
              An icon will be added to your device&apos;s app screen for quick access.
            </p>
          </div>
        </div>
        
        <div className="install-prompt-ios-steps">
          <div className="install-step">
            <Share className="w-5 h-5" />
            <span>Tap the share button</span>
          </div>
          <div className="install-step">
            <Plus className="w-5 h-5" />
            <span>Select &quot;Add to Home Screen&quot;</span>
          </div>
        </div>
      </div>
    );
  }

  // Standard install prompt for Android (mobile only)
  if (!isInstallable) return null;

  return (
    <div className="install-prompt">
      <button onClick={handleDismiss} className="install-prompt-close">
        <X className="w-4 h-4" />
      </button>
      
      <div className="install-prompt-content">
        <div className="install-prompt-icon">
          <Download className="w-6 h-6" />
        </div>
        
        <div className="install-prompt-text">
          <h3 className="install-prompt-title">Install Shadesense on your phone</h3>
          <p className="install-prompt-desc">
            An icon will be added to your device&apos;s app screen for quick access.
          </p>
        </div>
      </div>
      
      <button onClick={handleInstall} className="install-prompt-btn">
        Install
      </button>
    </div>
  );
};

export default InstallPrompt;
